import React, { useState, useEffect } from 'react';
import * as api from '../../api';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Spinner from '../../Plateforme/Atoms/Spinner';
import Section from '../Atoms/Section';
import H0 from '../Atoms/H0';
import GetProjectMainImage from '../Utils/GetProjectMainImage';
import { useNavigate, useLocation } from 'react-router-dom';
import { generateFD100KVDataURL } from '../Utils/generateFD100KV';
import { createUrlWithRedirect } from '../Utils/redirectUtils';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';

const CenteredContainer = styled.div`
    max-width: 1000px;
    margin: 0 auto;
    padding: 20px;
`;

const PageTitle = styled.div`
    text-align: center;
    margin-bottom: 10px;
`;

const Deadline = styled.div`
    text-align: center;
    color: #dc3545;
    font-weight: 500;
    font-size: 18px;
    margin-bottom: 30px;
`;

const BackLink = styled.div`
    margin-bottom: 20px;

    a {
        color: #007bff;
        text-decoration: none;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const ValidationContainer = styled.div`
    background-color: #f5f5f5;
    border: 2px solid #dc3545;
    border-radius: 0px;
    padding: 30px;
    margin: 30px 0;
`;

const ProjectValidationRow = styled.div`
    display: flex;
    gap: 30px;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const ProjectVisualSection = styled.div`
    flex: 0 0 350px;

    @media (max-width: 768px) {
        flex: 1;
    }
`;

const ProjectKeyVisual = styled.div`
    border: 2px solid #dc3545;
    background-color: white;
    padding: 15px;
`;

const ProjectHeader = styled.div`
    color: #dc3545;
    margin-bottom: 10px;

    .project-name {
        font-weight: bold;
        font-size: 18px;
    }

    .country {
        font-style: italic;
    }

    .creator {
        font-weight: 500;
    }
`;

const ProjectImage = styled.div`
    height: 250px;
    background-color: #ddd;
    background-image: ${props => props.src ? `url(${props.src})` : 'none'};
    background-size: cover;
    background-position: center;
    margin-bottom: 15px;
`;

const FD100Badge = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #333;

    .left {
        color: #dc3545;
        font-weight: bold;
    }
`;

const FabricantsBox = styled.div`
    border: 2px solid #dc3545;
    background-color: white;
    padding: 15px;
    margin-top: 20px;

    .title {
        font-weight: bold;
        margin-bottom: 10px;
    }

    ul {
        margin: 0;
        padding-left: 20px;
    }

    li {
        margin-bottom: 5px;
    }
`;

const ValidationSection = styled.div`
    flex: 1;
`;

const ValidationItem = styled.div`
    display: flex;
    align-items: flex-start;
    margin-bottom: 20px;

    .checkbox-container {
        margin-right: 15px;
        padding-top: 2px;
    }

    .content {
        flex: 1;
    }

    .label {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 5px;

        input[type="checkbox"] {
            width: 20px;
            height: 20px;
            accent-color: #28a745;
            cursor: pointer;
        }

        span {
            font-weight: 500;
        }
    }

    .modify-link {
        color: #007bff;
        text-decoration: underline;
        cursor: pointer;
        font-size: 14px;
        margin-left: 30px;

        &:hover {
            color: #0056b3;
        }
    }
`;

const KeyVisualDownloads = styled.div`
    display: flex;
    gap: 20px;
    margin-top: 30px;
    flex-wrap: wrap;
`;

const DownloadCard = styled.div`
    flex: 1;
    min-width: 200px;
    border: 1px solid #ddd;
    padding: 10px;
    text-align: center;

    img {
        max-width: 100%;
        height: 150px;
        object-fit: cover;
        margin-bottom: 10px;
    }

    .download-link {
        color: #007bff;
        text-decoration: underline;
        cursor: pointer;
        font-size: 14px;

        &:hover {
            color: #0056b3;
        }
    }
`;

const ContactNote = styled.div`
    margin-top: 20px;
    padding: 15px;
    background-color: #e7f3ff;
    border: 1px solid #007bff;
    border-radius: 5px;
    font-size: 14px;
    color: #004085;

    a {
        color: #007bff;
        text-decoration: underline;
        font-weight: 500;
    }
`;

const NoProjectsMessage = styled.div`
    text-align: center;
    padding: 40px;
    color: #666;
    font-size: 16px;
`;

const EditField = styled.div`
    margin-top: 8px;
    margin-left: 30px;

    input {
        width: 100%;
        max-width: 300px;
        padding: 8px 12px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 14px;

        &:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }
    }
`;

const EditActions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 8px;
`;

const SaveButton = styled.button`
    padding: 6px 16px;
    background-color: #28a745;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;

    &:hover {
        background-color: #218838;
    }
`;

const CancelButton = styled.button`
    padding: 6px 16px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;

    &:hover {
        background-color: #5a6268;
    }
`;

const CurrentValue = styled.span`
    display: inline-block;
    margin-left: 5px;
    color: #333;
    font-weight: bold;
`;

const FabricantEditRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;

    input {
        flex: 1;
        max-width: 250px;
        padding: 6px 10px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 14px;

        &:focus {
            outline: none;
            border-color: #007bff;
        }
    }
`;

const AddFabricantButton = styled.button`
    padding: 6px 12px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    margin-top: 8px;

    &:hover {
        background-color: #0056b3;
    }
`;

const RemoveFabricantButton = styled.button`
    padding: 4px 8px;
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
        background-color: #c82333;
    }
`;

// List of winner project IDs
const WINNER_PROJECT_IDS = [
    '67a332e21b103d938bafb0dc',
    '67a0b8331b103d938bafa954',
    '67aa1011740d844a3d633e93',
    '6829daa5754f51df2ef9141c',
    '67e6c18043d5609350e381a8',
    '67b5e6146890f1a147192905',
    '67be4a58b1ba28c3144aeb73',
    '67bf2330b1ba28c3144af8d2',
    '67c090dcdb48cb16a13c5b43',
    '677268097ec5d90c89d6fe63',
    '67c6b416db48cb16a13c73c7',
    '67ab652c07736060460b3b75',
    '68348986754f51df2ef9b87c',
    '67d15108d95ccf24eaf191df',
    '67d7d38c43d5609350e242a1',
    '67d82d2243d5609350e24c5e',
    '662adc507159e5b1b1da7c75',
    '67d8718c43d5609350e25c38',
    '67d1977243d5609350e218f2',
    '682b5c5f754f51df2ef93bbc',
    '67da910a43d5609350e274d7',
    '67e1864b43d5609350e2baeb',
    '67daebf343d5609350e27a9e',
    '67dd585043d5609350e28e57',
    '67dfeb0e43d5609350e2a360',
    '67e1452a43d5609350e2b066',
    '678675e2de3ed4a7001ab532',
    '67e2d05943d5609350e2cf97',
    '67e3e1cb43d5609350e304e5',
    '67e3e81543d5609350e308de',
    '67e3f02643d5609350e30a0a',
    '67e42d7043d5609350e32539',
    '67e42e5543d5609350e32570',
    '67e45c9843d5609350e329c0',
    '67e529f743d5609350e33a8c',
    '67e53aab43d5609350e340c3',
    '67e56ab043d5609350e3490f',
    '67e5764d43d5609350e34cb5',
    '67e57f5143d5609350e3519a',
    '67e5864c43d5609350e353a3',
    '67e58da443d5609350e3561d',
    '67e58ebc43d5609350e35665',
    '67e5ab6b43d5609350e35905',
    '67e6828d43d5609350e36b6a',
    '67e6acaf43d5609350e37832',
    '67e51ddb43d5609350e33667',
    '67e6c0a843d5609350e380cf',
    '67e6d53a43d5609350e38725',
    '67e6dfff43d5609350e38900',
    '67e7003c43d5609350e38bfc',
    '6834625c754f51df2ef9a4b1',
    '67ea99b76b7945ed1a576bae',
    '67f90250a0262e085d37088f',
    '67e1741a43d5609350e2b608',
    '67fe7a6c9167c049fff4e37e',
    '67ab6ade07736060460b3c16',
    '6810a343264e7745556acae1',
    '6800f4c59167c049fff51f5d',
    '6800b8529167c049fff50e47',
    '6800e76e9167c049fff5197f',
    '6800ef849167c049fff51c6f',
    '6800fad59167c049fff52224',
    '680113739167c049fff52d73',
    '680106139167c049fff525af',
    '680125219167c049fff53405',
    '680957ab9167c049fff5a7c2',
    '681164d36c8fbfe5e3c91e9e',
    '68020ef79167c049fff54015',
    '68025d069167c049fff56132',
    '680b728c9167c049fff5c8f7',
    '680249249167c049fff55a19',
    '68024ed89167c049fff55b90',
    '68025b1a9167c049fff56037',
    '680270379167c049fff566f5',
    '680274c09167c049fff567c8',
    '680275419167c049fff567de',
    '68027b389167c049fff56954',
    '680291609167c049fff56cad',
    '6802a3fc9167c049fff56f5f',
    '6849787b17dde4ca4c4bf043',
    '6849435a17dde4ca4c4be251',
    '6807cb759167c049fff58dd4',
    '6808c3f59167c049fff599c7',
    '6808c3859167c049fff599b1',
    '680fa4b19167c049fff5deee',
    '6801e2f49167c049fff53ce1',
    '680a54a09167c049fff5bbc4',
    '680b94b29167c049fff5cd49',
    '6810df4a264e7745556ad75d',
    '680fa3f89167c049fff5deb1',
    '680fa844a71ddf126b77b977',
    '680fa9f09167c049fff5e027',
    '6810c9b9264e7745556acf4d',
    '681125b66c8fbfe5e3c91c62',
    '6818cac19e656d77bc6c72fa',
    '6818d27e9e656d77bc6c75a3',
    '6818dbdf9e656d77bc6c78a0',
    '682d97b2754f51df2ef96070',
    '67fccffaa0262e085d370ddc',
    '680271809167c049fff5674a'
];

const InformationsAValiderFD100 = (props) => {
    const { i18n } = useTranslation('common');
    const [loading, setLoading] = useState(true);
    const [winningProjects, setWinningProjects] = useState([]);
    const [projectUsers, setProjectUsers] = useState({}); // Store user data for each project
    const [validations, setValidations] = useState({});
    const [kvImages, setKvImages] = useState({});
    const [generatingKvs, setGeneratingKvs] = useState(false);
    const [editingField, setEditingField] = useState({}); // { projectId: 'fieldName' }
    const [editValues, setEditValues] = useState({}); // { projectId: { fieldName: value } }
    const [savedEdits, setSavedEdits] = useState({}); // { projectId: { fieldName: savedValue } } - persisted edits
    const [hasGeneratedKvs, setHasGeneratedKvs] = useState(false);
    const navigate = useNavigate();

    // FD100 2025 event ID
    const eventId = '67590da5499bcc995c210b40';

    const isAdmin = props.fullUser?.user?.role === 'admin';

    const location = useLocation();

    useEffect(() => {
        if (props.fullUserLoading) return;
        if (!props.fullUser?.user) {
            navigate(createUrlWithRedirect('/login', location.pathname));
            return;
        }

        fetchWinningProjects();
    }, [props.fullUserLoading, props.fullUser, navigate]);

    // Fetch existing tracking data from Supabase to restore validations and edits
    // Returns the restored edits so they can be used immediately for KV generation
    const fetchTrackingData = async (projectIds) => {
        try {
            const userId = props.fullUser?.user?._id;
            if (!userId || projectIds.length === 0) return {};

            const { data, error } = await supabase
                .from('fd100_laureats_tracking')
                .select('*')
                .eq('user_id', userId)
                .eq('event_id', eventId)
                .in('project_id', projectIds);

            if (error) {
                console.error('Error fetching tracking data:', error);
                return {};
            }

            if (data && data.length > 0) {
                const restoredValidations = {};
                const restoredEdits = {};

                data.forEach(record => {
                    const projectId = record.project_id;

                    // Restore validation states
                    restoredValidations[projectId] = {
                        projectName: record.validated_project_name || false,
                        creatorName: record.validated_creator_name || false,
                        country: record.validated_country || false,
                        school: record.validated_school || false,
                        keyVisual: record.validated_key_visual || false
                    };

                    // Restore individual fabricant validations from jsonb array
                    const validatedFabricantIndices = record.validated_fabricants || [];
                    if (Array.isArray(validatedFabricantIndices)) {
                        validatedFabricantIndices.forEach(idx => {
                            restoredValidations[projectId][`fabricant_${idx}`] = true;
                        });
                    }

                    // Restore edited values (only if they exist)
                    const edits = {};
                    if (record.edited_project_name) edits.projectName = record.edited_project_name;
                    if (record.edited_creator_name) edits.creatorName = record.edited_creator_name;
                    if (record.edited_country) edits.country = record.edited_country;
                    if (record.edited_school) edits.school = record.edited_school;
                    if (record.edited_fabricants) {
                        try {
                            edits.fabricants = JSON.parse(record.edited_fabricants);
                        } catch {
                            edits.fabricants = null;
                        }
                    }

                    if (Object.keys(edits).length > 0) {
                        restoredEdits[projectId] = edits;
                    }
                });

                setValidations(restoredValidations);
                setSavedEdits(restoredEdits);

                console.log('Restored tracking data:', { restoredValidations, restoredEdits });
                return restoredEdits; // Return for immediate use
            }
            return {};
        } catch (error) {
            console.error('Error fetching tracking data:', error);
            return {};
        }
    };

    const fetchWinningProjects = async () => {
        try {
            // Prevent duplicate generation runs
            if (generatingKvs) {
                console.log('Already generating KVs, skipping duplicate call');
                return;
            }

            setLoading(true);

            let projectsToGenerate = [];
            let projectUsersMapping = {};

            // For admin users, fetch a random winning project for preview
            if (isAdmin) {
                const response = await api.getParticipatingUsersWithData({ eventId: eventId });

                let _allProjects = [];
                response.data.forEach(user => {
                    if (user.participation.data.projets) {
                        user.participation.data.projets.forEach(project_id => {
                            const project = user.company.projects.find((projet) => projet._id === project_id);
                            if (project && project.status === "validé" && WINNER_PROJECT_IDS.includes(project._id)) {
                                _allProjects.push({
                                    project,
                                    user
                                });
                            }
                        });
                    }
                });

                // Pick 1 random project to display
                if (_allProjects.length > 0) {
                    const randomIndex = Math.floor(Math.random() * _allProjects.length);
                    const selected = _allProjects[randomIndex];
                    setWinningProjects([selected.project]);
                    projectUsersMapping = { [selected.project._id]: selected.user };
                    setProjectUsers(projectUsersMapping);
                    projectsToGenerate = [selected.project];
                }

                setLoading(false);
            } else {
                // Get user participations
                const userParticipations = await api.getUserParticipations({ userid: props.fullUser.user._id });

                // Find the participation for FD100 2025
                const eventParticipation = userParticipations.data.find(p => p.eventId === eventId);

                if (eventParticipation && eventParticipation.data && eventParticipation.data.projets) {
                    // Filter for winning projects only
                    const enrichedWinningProjects = eventParticipation.data.projets
                        .map(projectId => {
                            if (typeof projectId === 'object' && projectId !== null) {
                                return projectId;
                            }
                            const projectFromCompany = props.fullUser.company?.projects?.find(p => p._id === projectId);
                            return projectFromCompany;
                        })
                        .filter(project => project && WINNER_PROJECT_IDS.includes(project._id))
                        .filter(Boolean);

                    setWinningProjects(enrichedWinningProjects);
                    projectsToGenerate = enrichedWinningProjects;

                    // Store the current user as the project owner for all these projects
                    enrichedWinningProjects.forEach(project => {
                        projectUsersMapping[project._id] = props.fullUser;
                    });
                    setProjectUsers(projectUsersMapping);
                }
                setLoading(false);
            }

            // Fetch existing tracking data from Supabase to restore validations and edits
            let restoredEdits = {};
            if (projectsToGenerate.length > 0) {
                const projectIds = projectsToGenerate.map(p => p._id);
                restoredEdits = await fetchTrackingData(projectIds);
            }

            // Generate KV images for all winning projects (using restored edits if any)
            if (projectsToGenerate.length > 0 && !hasGeneratedKvs) {
                setGeneratingKvs(true);
                setHasGeneratedKvs(true);

                for (const project of projectsToGenerate) {
                    try {
                        // Get user data from the mapping we just built
                        const userData = projectUsersMapping[project._id];

                        if (!userData) {
                            console.error(`No user data found for project ${project._id}`);
                            continue;
                        }

                        // Apply restored edits if any exist for this project
                        const projectEdits = restoredEdits[project._id] || {};
                        const editedProject = {
                            ...project,
                            name_en: projectEdits.projectName || project.name_en,
                            name: projectEdits.projectName || project.name
                        };

                        // Apply country edit if present
                        if (projectEdits.country) {
                            editedProject.adresse = projectEdits.country;
                        }

                        // Create modified user data for creator name if edited
                        let editedUserData = userData;
                        if (projectEdits.creatorName) {
                            const nameParts = projectEdits.creatorName.split(' ');
                            editedUserData = {
                                ...userData,
                                hsProfile: {
                                    ...userData.hsProfile,
                                    firstname: nameParts[0] || '',
                                    lastname: nameParts.slice(1).join(' ') || ''
                                },
                                hsCompany: {
                                    ...userData.hsCompany,
                                    properties: {
                                        ...userData.hsCompany?.properties,
                                        name: projectEdits.creatorName
                                    }
                                }
                            };
                        }

                        // Prepare project data for frontend generation
                        const projectData = {
                            project: editedProject,
                            user: editedUserData
                        };

                        // Generate using frontend canvas
                        const normalDataUrl = await generateFD100KVDataURL(projectData, false);
                        const invertedDataUrl = await generateFD100KVDataURL(projectData, true);

                        // Update state immediately after each project completes
                        setKvImages(prev => ({
                            ...prev,
                            [project._id]: {
                                normal: normalDataUrl,
                                inverted: invertedDataUrl
                            }
                        }));
                    } catch (error) {
                        console.error(`Error generating KV for project ${project._id}:`, error);
                    }
                }
                setGeneratingKvs(false);
            }
        } catch (error) {
            console.error("Error fetching winning projects:", error);
        } finally {
            setLoading(false);
        }
    };

    const getCreatorName = (project) => {
        // For admin preview, use the stored user data
        const user = projectUsers[project._id] || props.fullUser;

        // Check if candidating as person
        if (project.candidate_as_person_fd100_25 === "oui") {
            const firstName = user?.hsProfile?.firstname || '';
            const lastName = user?.hsProfile?.lastname || '';
            return `${firstName} ${lastName}`.trim();
        }
        // Default: company name
        return user?.hsCompany?.properties?.name || '';
    };

    const getProjectCountry = (project) => {
        // Use the same logic as generateFD100KV.js to extract country from project address
        let countryFinal = '';
        if (project.adresse) {
            const addressParts = project.adresse.split(',');
            const country = addressParts.length > 0 ? addressParts[addressParts.length - 1].trim() : '';
            const countryClean = country.replace(/[0-9]/g, '').trim();

            const countryTranslations = {
                'japon': 'Japan',
                'espagne': 'Spain'
            };
            countryFinal = countryTranslations[countryClean.toLowerCase()] || countryClean;
        }
        return countryFinal;
    };

    const getFabricants = (project) => {
        if (!project.fabricants) return [];
        try {
            const parsed = JSON.parse(project.fabricants);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const handleValidationChange = async (projectId, field) => {
        const newValue = !validations[projectId]?.[field];

        const newValidations = {
            ...validations[projectId],
            [field]: newValue
        };

        setValidations(prev => ({
            ...prev,
            [projectId]: newValidations
        }));

        // Show toast notification immediately
        if (newValue) {
            toast.success(i18n.language === 'fr' ? 'Validation enregistrée' : 'Validation saved');
        } else {
            toast(i18n.language === 'fr' ? 'Validation retirée' : 'Validation removed', { icon: '↩️' });
        }

        // Map field names to Supabase column names
        const fieldMapping = {
            'projectName': 'validated_project_name',
            'creatorName': 'validated_creator_name',
            'country': 'validated_country',
            'school': 'validated_school',
            'fabricants': 'validated_fabricants',
            'keyVisual': 'validated_key_visual'
        };

        // Handle individual fabricant validations (fabricant_0, fabricant_1, etc.)
        if (field.startsWith('fabricant_')) {
            const fabIndex = parseInt(field.split('_')[1], 10);

            // Build array of validated fabricant indices
            const project = winningProjects.find(p => p._id === projectId);
            const fabricants = getFabricants(project);
            const validatedIndices = [];

            fabricants.forEach((_, idx) => {
                const isValidated = idx === fabIndex ? newValue : newValidations[`fabricant_${idx}`];
                if (isValidated) {
                    validatedIndices.push(idx);
                }
            });

            // Update with array of validated indices
            await updateTrackingData(projectId, {
                validated_fabricants: validatedIndices,
                last_validation_update: new Date().toISOString()
            });
            return;
        }

        const supabaseField = fieldMapping[field];
        if (!supabaseField) return;

        // Update tracking in Supabase
        await updateTrackingData(projectId, { [supabaseField]: newValue, last_validation_update: new Date().toISOString() });
    };

    const updateTrackingData = async (projectId, updates) => {
        try {
            const user = projectUsers[projectId] || props.fullUser;
            const project = winningProjects.find(p => p._id === projectId);

            // Try to upsert the tracking record
            const { error } = await supabase
                .from('fd100_laureats_tracking')
                .upsert({
                    user_id: user?.user?._id || props.fullUser?.user?._id,
                    project_id: projectId,
                    event_id: eventId,
                    user_email: user?.hsProfile?.email || user?.user?.email,
                    user_name: `${user?.hsProfile?.firstname || ''} ${user?.hsProfile?.lastname || ''}`.trim(),
                    company_name: user?.hsCompany?.properties?.name || '',
                    project_name: project?.name_en || project?.name,
                    ...updates,
                    updated_at: new Date().toISOString()
                }, {
                    onConflict: 'user_id,project_id,event_id'
                });

            if (error) {
                console.error('Error updating tracking data:', error);
            }
        } catch (error) {
            console.error('Error updating tracking:', error);
        }
    };

    const handleModifyClick = (projectId, field, currentValue) => {
        setEditingField(prev => ({ ...prev, [projectId]: field }));
        setEditValues(prev => ({
            ...prev,
            [projectId]: {
                ...prev[projectId],
                [field]: currentValue
            }
        }));
    };

    const handleEditChange = (projectId, field, value) => {
        setEditValues(prev => ({
            ...prev,
            [projectId]: {
                ...prev[projectId],
                [field]: value
            }
        }));
    };

    const handleSaveEdit = async (projectId, field) => {
        const newValue = editValues[projectId]?.[field];
        console.log('Saving:', projectId, field, newValue);

        // Map field names to Supabase column names for edits
        const editFieldMapping = {
            'projectName': 'edited_project_name',
            'creatorName': 'edited_creator_name',
            'country': 'edited_country',
            'school': 'edited_school',
            'fabricants': 'edited_fabricants'
        };

        const supabaseField = editFieldMapping[field];
        if (supabaseField) {
            // For fabricants, store as JSON
            const valueToStore = field === 'fabricants' ? JSON.stringify(newValue) : newValue;
            await updateTrackingData(projectId, {
                [supabaseField]: valueToStore,
                last_edit_update: new Date().toISOString()
            });
        }

        // Save the edit to display updated value
        setSavedEdits(prev => ({
            ...prev,
            [projectId]: {
                ...prev[projectId],
                [field]: newValue
            }
        }));

        // Show success toast
        toast.success(i18n.language === 'fr' ? 'Modification enregistrée' : 'Change saved');

        // Close the edit mode
        setEditingField(prev => ({ ...prev, [projectId]: null }));

        // Regenerate KV if project name, creator name, or country was edited
        if (['projectName', 'creatorName', 'country'].includes(field)) {
            // Pass the new edits including the one just saved
            const updatedEdits = {
                ...savedEdits[projectId],
                [field]: newValue
            };
            regenerateKV(projectId, updatedEdits);
        }
    };

    const regenerateKV = async (projectId, editsOverride = null) => {
        try {
            const project = winningProjects.find(p => p._id === projectId);
            const userData = projectUsers[projectId] || props.fullUser;

            if (!project || !userData) return;

            // Use passed edits or fall back to savedEdits state
            const projectEdits = editsOverride || savedEdits[projectId] || {};
            const editedProject = {
                ...project,
                // Apply saved edits
                name_en: projectEdits.projectName || project.name_en,
                name: projectEdits.projectName || project.name
            };

            // Apply country edit if present
            if (projectEdits.country) {
                editedProject.adresse = projectEdits.country; // Will be used by KV generator
            }

            // Create modified user data for creator name
            const editedUserData = { ...userData };
            if (projectEdits.creatorName) {
                // Split the name if possible
                const nameParts = projectEdits.creatorName.split(' ');
                editedUserData.hsProfile = {
                    ...editedUserData.hsProfile,
                    firstname: nameParts[0] || '',
                    lastname: nameParts.slice(1).join(' ') || ''
                };
                editedUserData.hsCompany = {
                    ...editedUserData.hsCompany,
                    properties: {
                        ...editedUserData.hsCompany?.properties,
                        name: projectEdits.creatorName
                    }
                };
            }

            const projectData = {
                project: editedProject,
                user: editedUserData
            };

            // Regenerate KV images
            toast.loading(i18n.language === 'fr' ? 'Régénération du Key Visual...' : 'Regenerating Key Visual...', { id: 'kv-regen' });

            const normalDataUrl = await generateFD100KVDataURL(projectData, false);
            const invertedDataUrl = await generateFD100KVDataURL(projectData, true);

            setKvImages(prev => ({
                ...prev,
                [projectId]: {
                    normal: normalDataUrl,
                    inverted: invertedDataUrl
                }
            }));

            toast.success(i18n.language === 'fr' ? 'Key Visual mis à jour' : 'Key Visual updated', { id: 'kv-regen' });
        } catch (error) {
            console.error('Error regenerating KV:', error);
            toast.error(i18n.language === 'fr' ? 'Erreur lors de la régénération' : 'Regeneration error', { id: 'kv-regen' });
        }
    };

    const handleCancelEdit = (projectId) => {
        setEditingField(prev => ({ ...prev, [projectId]: null }));
    };

    const handleFabricantsChange = (projectId, index, value) => {
        setEditValues(prev => {
            const currentFabs = prev[projectId]?.fabricants || [];
            const newFabs = [...currentFabs];
            newFabs[index] = value;
            return {
                ...prev,
                [projectId]: {
                    ...prev[projectId],
                    fabricants: newFabs
                }
            };
        });
    };

    const handleAddFabricant = (projectId) => {
        setEditValues(prev => {
            const currentFabs = prev[projectId]?.fabricants || [];
            return {
                ...prev,
                [projectId]: {
                    ...prev[projectId],
                    fabricants: [...currentFabs, '']
                }
            };
        });
    };

    const handleRemoveFabricant = (projectId, index) => {
        setEditValues(prev => {
            const currentFabs = prev[projectId]?.fabricants || [];
            const newFabs = currentFabs.filter((_, i) => i !== index);
            return {
                ...prev,
                [projectId]: {
                    ...prev[projectId],
                    fabricants: newFabs
                }
            };
        });
    };

    const downloadKV = (imageUrl, projectName, inverted) => {
        // Extract filename from imageUrl
        const filename = imageUrl.split('/').pop().split('.')[0];
        const version = inverted ? 'bleu' : 'gris';
        const downloadUrl = `${imageUrl}?downloadfile=true&filename=${encodeURIComponent(projectName)}_${version}`;

        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading || generatingKvs) {
        return (
            <Section className="padding">
                <br/><br/>
                <Spinner />
            </Section>
        );
    }

    return (
        <Section className="padding">
            <CenteredContainer>
                <BackLink>
                    <a onClick={() => navigate('/mywinningprojects')}>
                        &larr; {i18n.language === 'fr' ? 'Retour aux projets lauréats' : 'Back to winning projects'}
                    </a>
                </BackLink>

                <PageTitle>
                    <H0>{i18n.language === 'fr' ? 'Informations & Key Visual à valider' : 'Information & Key Visual to validate'}</H0>
                </PageTitle>
                <Deadline>
                    {i18n.language === 'fr' ? '- avant le 09.12.25' : '- before 09.12.25'}
                </Deadline>

                {winningProjects.length === 0 ? (
                    <NoProjectsMessage>
                        <p className="thin">
                            {i18n.language === 'fr'
                                ? 'Aucun projet lauréat trouvé.'
                                : 'No winning projects found.'}
                        </p>
                    </NoProjectsMessage>
                ) : (
                    winningProjects.map((project) => {
                        const fabricants = getFabricants(project);
                        const projectValidations = validations[project._id] || {};

                        return (
                            <ValidationContainer key={project._id}>
                                <ProjectValidationRow>
                                    <ProjectVisualSection>
                                        <ProjectKeyVisual>
                                            {kvImages[project._id]?.normal ? (
                                                <img
                                                    src={kvImages[project._id].normal}
                                                    alt="Key Visual"
                                                    style={{width: '100%', height: 'auto', display: 'block'}}
                                                />
                                            ) : (
                                                <>
                                                    <ProjectHeader>
                                                        <div className="project-name">{project.name_en || project.name || "Projet sans nom"}</div>
                                                        <div className="country">{getProjectCountry(project)}</div>
                                                        <div className="creator">{getCreatorName(project)}</div>
                                                    </ProjectHeader>
                                                    <ProjectImage src={GetProjectMainImage(project) || ''} />
                                                    <FD100Badge>
                                                        <div className="left">
                                                            Le<br/>
                                                            <strong>FRENCH<br/>DESIGN</strong><br/>
                                                            100<br/>
                                                            AWARDS<br/>
                                                            4e édition
                                                        </div>
                                                        <div className="right">
                                                            MINISTÈRE<br/>
                                                            DE LA CULTURE
                                                        </div>
                                                    </FD100Badge>
                                                </>
                                            )}
                                        </ProjectKeyVisual>

                                        <FabricantsBox>
                                            <div className="title">LISTE DES FABRICANTS:</div>
                                            {(() => {
                                                const displayFabricants = savedEdits[project._id]?.fabricants || fabricants.map(f => f.name || f);
                                                return displayFabricants.length > 0 ? (
                                                    <ul>
                                                        {displayFabricants.map((fab, idx) => (
                                                            <li key={idx}>- {typeof fab === 'object' ? fab.name : fab}</li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <div style={{color: '#666', fontStyle: 'italic'}}>
                                                        {i18n.language === 'fr' ? 'Aucun fabricant renseigné' : 'No manufacturers listed'}
                                                    </div>
                                                );
                                            })()}
                                        </FabricantsBox>
                                    </ProjectVisualSection>

                                    <ValidationSection>
                                        <ValidationItem>
                                            <div className="content">
                                                <div className="label">
                                                    <input
                                                        type="checkbox"
                                                        checked={projectValidations.projectName || false}
                                                        onChange={() => handleValidationChange(project._id, 'projectName')}
                                                    />
                                                    <span>{i18n.language === 'fr' ? 'Je valide le nom de projet' : 'I validate the project name'}:<CurrentValue>{savedEdits[project._id]?.projectName || project.name_en || project.name || (i18n.language === 'fr' ? 'Non renseigné' : 'Not specified')}</CurrentValue></span>
                                                </div>
                                                {editingField[project._id] === 'projectName' ? (
                                                    <EditField>
                                                        <input
                                                            type="text"
                                                            value={editValues[project._id]?.projectName || ''}
                                                            onChange={(e) => handleEditChange(project._id, 'projectName', e.target.value)}
                                                            placeholder={i18n.language === 'fr' ? 'Nom du projet' : 'Project name'}
                                                        />
                                                        <EditActions>
                                                            <SaveButton onClick={() => handleSaveEdit(project._id, 'projectName')}>
                                                                {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
                                                            </SaveButton>
                                                            <CancelButton onClick={() => handleCancelEdit(project._id)}>
                                                                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                                                            </CancelButton>
                                                        </EditActions>
                                                    </EditField>
                                                ) : (
                                                    <div className="modify-link" onClick={() => handleModifyClick(project._id, 'projectName', savedEdits[project._id]?.projectName || project.name_en || project.name || '')}>
                                                        &rarr; {i18n.language === 'fr' ? 'Je modifie le nom de projet' : 'I modify the project name'}
                                                    </div>
                                                )}
                                            </div>
                                        </ValidationItem>

                                        <ValidationItem>
                                            <div className="content">
                                                <div className="label">
                                                    <input
                                                        type="checkbox"
                                                        checked={projectValidations.creatorName || false}
                                                        onChange={() => handleValidationChange(project._id, 'creatorName')}
                                                    />
                                                    <span>{i18n.language === 'fr' ? 'Je valide le nom du créateur' : 'I validate the creator name'}:<CurrentValue>{savedEdits[project._id]?.creatorName || getCreatorName(project) || (i18n.language === 'fr' ? 'Non renseigné' : 'Not specified')}</CurrentValue></span>
                                                </div>
                                                {editingField[project._id] === 'creatorName' ? (
                                                    <EditField>
                                                        <input
                                                            type="text"
                                                            value={editValues[project._id]?.creatorName || ''}
                                                            onChange={(e) => handleEditChange(project._id, 'creatorName', e.target.value)}
                                                            placeholder={i18n.language === 'fr' ? 'Nom du créateur' : 'Creator name'}
                                                        />
                                                        <EditActions>
                                                            <SaveButton onClick={() => handleSaveEdit(project._id, 'creatorName')}>
                                                                {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
                                                            </SaveButton>
                                                            <CancelButton onClick={() => handleCancelEdit(project._id)}>
                                                                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                                                            </CancelButton>
                                                        </EditActions>
                                                    </EditField>
                                                ) : (
                                                    <div className="modify-link" onClick={() => handleModifyClick(project._id, 'creatorName', savedEdits[project._id]?.creatorName || getCreatorName(project))}>
                                                        &rarr; {i18n.language === 'fr' ? 'Je modifie le nom du créateur' : 'I modify the creator name'}
                                                    </div>
                                                )}
                                            </div>
                                        </ValidationItem>

                                        {project.type === 'espace' && (
                                            <ValidationItem>
                                                <div className="content">
                                                    <div className="label">
                                                        <input
                                                            type="checkbox"
                                                            checked={projectValidations.country || false}
                                                            onChange={() => handleValidationChange(project._id, 'country')}
                                                        />
                                                        <span>{i18n.language === 'fr' ? 'Je valide le pays du projet' : 'I validate the project country'}:<CurrentValue>{savedEdits[project._id]?.country || getProjectCountry(project)}</CurrentValue></span>
                                                    </div>
                                                    {editingField[project._id] === 'country' ? (
                                                        <EditField>
                                                            <input
                                                                type="text"
                                                                value={editValues[project._id]?.country || ''}
                                                                onChange={(e) => handleEditChange(project._id, 'country', e.target.value)}
                                                                placeholder={i18n.language === 'fr' ? 'Pays' : 'Country'}
                                                            />
                                                            <EditActions>
                                                                <SaveButton onClick={() => handleSaveEdit(project._id, 'country')}>
                                                                    {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
                                                                </SaveButton>
                                                                <CancelButton onClick={() => handleCancelEdit(project._id)}>
                                                                    {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                                                                </CancelButton>
                                                            </EditActions>
                                                        </EditField>
                                                    ) : (
                                                        <div className="modify-link" onClick={() => handleModifyClick(project._id, 'country', savedEdits[project._id]?.country || getProjectCountry(project))}>
                                                            &rarr; {i18n.language === 'fr' ? 'Je modifie le pays du projet' : 'I modify the project country'}
                                                        </div>
                                                    )}
                                                </div>
                                            </ValidationItem>
                                        )}

                                        <ValidationItem>
                                            <div className="content">
                                                <div className="label">
                                                    <input
                                                        type="checkbox"
                                                        checked={projectValidations.school || false}
                                                        onChange={() => handleValidationChange(project._id, 'school')}
                                                    />
                                                    <span>{i18n.language === 'fr' ? 'Je valide la formation du créateur' : 'I validate the creator\'s education'}:<CurrentValue>{savedEdits[project._id]?.school || projectUsers[project._id]?.hsProfile?.school || props.fullUser?.hsProfile?.school || (i18n.language === 'fr' ? 'Non renseignée' : 'Not specified')}</CurrentValue></span>
                                                </div>
                                                {editingField[project._id] === 'school' ? (
                                                    <EditField>
                                                        <input
                                                            type="text"
                                                            value={editValues[project._id]?.school || ''}
                                                            onChange={(e) => handleEditChange(project._id, 'school', e.target.value)}
                                                            placeholder={i18n.language === 'fr' ? 'École' : 'School'}
                                                        />
                                                        <EditActions>
                                                            <SaveButton onClick={() => handleSaveEdit(project._id, 'school')}>
                                                                {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
                                                            </SaveButton>
                                                            <CancelButton onClick={() => handleCancelEdit(project._id)}>
                                                                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                                                            </CancelButton>
                                                        </EditActions>
                                                    </EditField>
                                                ) : (
                                                    <div className="modify-link" onClick={() => handleModifyClick(project._id, 'school', savedEdits[project._id]?.school || projectUsers[project._id]?.hsProfile?.school || props.fullUser?.hsProfile?.school || '')}>
                                                        &rarr; {i18n.language === 'fr' ? 'Je modifie la formation du créateur' : 'I modify the creator\'s education'}
                                                    </div>
                                                )}
                                            </div>
                                        </ValidationItem>

                                        <ValidationItem>
                                            <div className="content">
                                                <div className="label">
                                                    <span style={{fontWeight: 500}}>{i18n.language === 'fr' ? 'Validation des fabricants :' : 'Manufacturers validation:'}</span>
                                                </div>
                                                {editingField[project._id] === 'fabricants' ? (
                                                    <EditField>
                                                        {(editValues[project._id]?.fabricants || []).map((fab, idx) => (
                                                            <FabricantEditRow key={idx}>
                                                                <input
                                                                    type="text"
                                                                    value={fab}
                                                                    onChange={(e) => handleFabricantsChange(project._id, idx, e.target.value)}
                                                                    placeholder={i18n.language === 'fr' ? 'Nom du fabricant' : 'Manufacturer name'}
                                                                />
                                                                <RemoveFabricantButton onClick={() => handleRemoveFabricant(project._id, idx)}>
                                                                    ✕
                                                                </RemoveFabricantButton>
                                                            </FabricantEditRow>
                                                        ))}
                                                        <AddFabricantButton onClick={() => handleAddFabricant(project._id)}>
                                                            + {i18n.language === 'fr' ? 'Ajouter un fabricant français' : 'Add French manufacturer'}
                                                        </AddFabricantButton>
                                                        <EditActions>
                                                            <SaveButton onClick={() => handleSaveEdit(project._id, 'fabricants')}>
                                                                {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
                                                            </SaveButton>
                                                            <CancelButton onClick={() => handleCancelEdit(project._id)}>
                                                                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                                                            </CancelButton>
                                                        </EditActions>
                                                    </EditField>
                                                ) : (() => {
                                                    // Use saved edits if available, otherwise use original fabricants
                                                    const displayFabricants = savedEdits[project._id]?.fabricants || fabricants.map(f => f.name || f);
                                                    return (
                                                    <>
                                                        {displayFabricants.length > 0 ? (
                                                            <div style={{marginLeft: '30px', marginTop: '10px'}}>
                                                                {displayFabricants.map((fab, idx) => (
                                                                    <div key={idx} style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px'}}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={projectValidations[`fabricant_${idx}`] || false}
                                                                            onChange={() => handleValidationChange(project._id, `fabricant_${idx}`)}
                                                                            style={{width: '18px', height: '18px', accentColor: '#28a745', cursor: 'pointer'}}
                                                                        />
                                                                        <span>{typeof fab === 'object' ? fab.name : fab}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div style={{color: '#666', fontStyle: 'italic', marginLeft: '30px', marginTop: '5px'}}>
                                                                {i18n.language === 'fr' ? 'Aucun fabricant renseigné' : 'No manufacturers listed'}
                                                            </div>
                                                        )}
                                                        <div className="modify-link" onClick={() => handleModifyClick(project._id, 'fabricants', displayFabricants)}>
                                                            &rarr; {i18n.language === 'fr' ? 'Je modifie la liste des fabricants' : 'I modify the manufacturers list'}
                                                        </div>
                                                    </>
                                                );
                                                })()}
                                                <div style={{marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', fontSize: '13px', color: '#856404'}}>
                                                    {i18n.language === 'fr' ? (
                                                        <>
                                                            <strong>Note :</strong> Si un fabricant doit rester confidentiel, contactez l'administrateur à <a href="mailto:giovagnoni@via.fr" style={{color: '#856404', fontWeight: 500}}>giovagnoni@via.fr</a>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <strong>Note:</strong> If a manufacturer must remain confidential, contact the administrator at <a href="mailto:giovagnoni@via.fr" style={{color: '#856404', fontWeight: 500}}>giovagnoni@via.fr</a>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </ValidationItem>

                                        <KeyVisualDownloads>
                                            <DownloadCard>
                                                {kvImages[project._id]?.normal ? (
                                                    <img src={kvImages[project._id].normal} alt="Gray KV" style={{height: '120px', width: 'auto', objectFit: 'contain'}} />
                                                ) : (
                                                    <ProjectImage src={GetProjectMainImage(project) || ''} style={{height: '120px', filter: 'grayscale(100%)'}} />
                                                )}
                                            </DownloadCard>
                                            <DownloadCard>
                                                {kvImages[project._id]?.inverted ? (
                                                    <img src={kvImages[project._id].inverted} alt="Blue KV" style={{height: '120px', width: 'auto', objectFit: 'contain'}} />
                                                ) : (
                                                    <ProjectImage src={GetProjectMainImage(project) || ''} style={{height: '120px'}} />
                                                )}
                                            </DownloadCard>
                                        </KeyVisualDownloads>

                                        <ContactNote>
                                            {i18n.language === 'fr' ? (
                                                <>
                                                    <strong>Un problème ou une erreur ?</strong>
                                                    <br />
                                                    Contactez l'administrateur à <a href="mailto:giovagnoni@via.fr">giovagnoni@via.fr</a>
                                                </>
                                            ) : (
                                                <>
                                                    <strong>Problem or error?</strong>
                                                    <br />
                                                    Contact the administrator at <a href="mailto:giovagnoni@via.fr">giovagnoni@via.fr</a>
                                                </>
                                            )}
                                        </ContactNote>
                                    </ValidationSection>
                                </ProjectValidationRow>
                            </ValidationContainer>
                        );
                    })
                )}
            </CenteredContainer>
        </Section>
    );
};

export default InformationsAValiderFD100;
